/**
 * Presentation Layer - useDataPreview Hook
 * データプレビュー表示のためのフック
 */

import { useState, useMemo, useCallback } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import type { CsvData } from '../../../../domain/medicalReservation/CsvData';
import type { ProcessingResult } from '../../../../domain/medicalReservation/ProcessingResult';
import { STATUS_COLORS, getStatusLabel } from '../../../../domain/medicalReservation/ReservationStatus';

/**
 * 表示モード
 */
export type DataDisplayMode = 'original' | 'filtered' | 'processed';

/**
 * プレビュー状態の型定義
 */
export interface DataPreviewState {
  /** 表示モード */
  displayMode: DataDisplayMode;
  /** 表示データ（行配列） */
  displayRows: Record<string, unknown>[];
  /** 表示カラム定義 */
  displayColumns: GridColDef[];
  /** 表示件数 */
  displayCount: number;
}

/**
 * プレビューアクションの型定義
 */
export interface DataPreviewActions {
  /** 表示モード切り替え */
  toggleDisplayMode: () => void;
  /** 特定のモードに設定 */
  setDisplayMode: (mode: DataDisplayMode) => void;
}

/**
 * フックの戻り値型
 */
export type UseDataPreviewReturn = [DataPreviewState, DataPreviewActions];

/**
 * フックのオプション
 */
export interface UseDataPreviewOptions {
  /** 元のCSVデータ */
  csvData: CsvData | null;
  /** フィルタリング後のデータ */
  filteredData: CsvData | null;
  /** 加工結果 */
  processingResult: ProcessingResult | null;
  /** 編集時のコールバック */
  onEditField?: (rowIndex: number, field: string) => void;
}

/**
 * CsvDataをDataGrid用の行データに変換
 */
const convertCsvDataToRows = (
  csvData: CsvData
): Record<string, unknown>[] => {
  return csvData.rows.map((row, index) => {
    const rowData: Record<string, unknown> = {
      id: index,
      _rowNumber: index + 1,
    };

    csvData.headers.forEach((header, colIndex) => {
      rowData[header] = row[colIndex] ?? '';
    });

    return rowData;
  });
};

/**
 * ProcessingResultをDataGrid用の行データに変換
 */
const convertProcessingResultToRows = (
  result: ProcessingResult
): Record<string, unknown>[] => {
  return result.processedData.map((reservation, index) => {
    const rowData: Record<string, unknown> = {
      id: reservation.id,
      _rowNumber: index + 1,
      _isStatusConverted: reservation.isStatusConverted,
      _isDoctorChanged: reservation.isDoctorChanged,
      _originalStatus: reservation.originalStatus,
      _originalDoctorName: reservation.originalDoctorName,
      ...reservation.rawData,
      // 加工済みの値で上書き
      ステータス: reservation.status,
      担当医師: reservation.doctorName,
    };

    return rowData;
  });
};

/**
 * CsvDataからカラム定義を生成
 */
const generateColumns = (
  headers: readonly string[],
  onEditField?: (rowIndex: number, field: string) => void
): GridColDef[] => {
  const columns: GridColDef[] = [
    {
      field: '_rowNumber',
      headerName: '行番号',
      width: 80,
      sortable: false,
    },
  ];

  headers.forEach((header) => {
    const column: GridColDef = {
      field: header,
      headerName: header,
      width: 150,
      flex: 1,
      minWidth: 100,
    };

    // ステータス列のカスタムレンダリング
    if (header === 'ステータス') {
      column.width = 160;
      column.renderCell = (params) => {
        const value = params.value as string;
        const color = STATUS_COLORS[value as keyof typeof STATUS_COLORS];
        return value;
      };
    }

    // 担当医師列のカスタムレンダリング
    if (header === '担当医師') {
      column.width = 120;
    }

    columns.push(column);
  });

  return columns;
};

/**
 * データプレビューフック
 */
export const useDataPreview = (
  options: UseDataPreviewOptions
): UseDataPreviewReturn => {
  const { csvData, filteredData, processingResult, onEditField } = options;

  // 表示モード状態
  const [displayMode, setDisplayMode] = useState<DataDisplayMode>('original');

  /**
   * 表示データの計算
   */
  const displayRows = useMemo<Record<string, unknown>[]>(() => {
    switch (displayMode) {
      case 'processed':
        if (processingResult) {
          return convertProcessingResultToRows(processingResult);
        }
        // 加工データがない場合はフィルター済みデータにフォールバック
        if (filteredData) {
          return convertCsvDataToRows(filteredData);
        }
        // フィルター済みデータもない場合は元データにフォールバック
        if (csvData) {
          return convertCsvDataToRows(csvData);
        }
        return [];

      case 'filtered':
        if (filteredData) {
          return convertCsvDataToRows(filteredData);
        }
        // フィルター済みデータがない場合は元データにフォールバック
        if (csvData) {
          return convertCsvDataToRows(csvData);
        }
        return [];

      case 'original':
      default:
        if (csvData) {
          return convertCsvDataToRows(csvData);
        }
        return [];
    }
  }, [displayMode, csvData, filteredData, processingResult]);

  /**
   * 表示カラムの計算
   */
  const displayColumns = useMemo<GridColDef[]>(() => {
    let headers: readonly string[] = [];

    switch (displayMode) {
      case 'processed':
        if (processingResult && processingResult.processedData.length > 0) {
          // ProcessedReservationのrawDataからヘッダーを取得
          const firstRow = processingResult.processedData[0];
          headers = Object.keys(firstRow.rawData);
        }
        break;
      case 'filtered':
        if (filteredData) {
          headers = filteredData.headers;
        }
        break;
      case 'original':
      default:
        if (csvData) {
          headers = csvData.headers;
        }
        break;
    }

    return generateColumns(headers, onEditField);
  }, [displayMode, csvData, filteredData, processingResult, onEditField]);

  /**
   * 表示件数の計算
   */
  const displayCount = useMemo<number>(() => {
    return displayRows.length;
  }, [displayRows]);

  /**
   * 表示モード切り替え（original ↔ processed のみ）
   */
  const toggleDisplayMode = useCallback(() => {
    setDisplayMode((current) => {
      // original ↔ processed のシンプルな切り替え
      if (current === 'processed') {
        return 'original';
      }
      // original または filtered → processed
      if (processingResult) {
        return 'processed';
      }
      return 'original';
    });
  }, [processingResult]);

  // 状態オブジェクト
  const state: DataPreviewState = {
    displayMode,
    displayRows,
    displayColumns,
    displayCount,
  };

  // アクションオブジェクト
  const actions: DataPreviewActions = {
    toggleDisplayMode,
    setDisplayMode,
  };

  return [state, actions];
};
